import { getBadgeColor, getBadgeLabel } from '@/utils/statusHelpers'
import { BasicPreviewLesson } from '@/types/teacher-request'
import { getLessonStatusStyle, resolveLessonStatus } from '@/utils/lessonStatusStyles'
import { sortLessonsByDateTime } from '@/utils/lessonSort'

interface TeacherBadgeProps {
  teacherCode: string
  status: string
  priorityRank?: number
  coverageInfo?: {
    lessonCount: number
    coverageScore?: number
    affinityBonus?: number
    coveredLessons: BasicPreviewLesson[]
    missingLessons: BasicPreviewLesson[]
  }
  is_next?: boolean
  /** cascade-v2: automatisch vom Matcher ergänzt (nicht vom Anfragenden gewählt) */
  auto_matched?: boolean
  /** cascade-v2: echter Name für Tooltip (nur wo nicht maskiert) */
  teacher_name?: string | null
}

export function TeacherBadge({ teacherCode, status, priorityRank, coverageInfo, is_next, auto_matched, teacher_name }: TeacherBadgeProps) {
  const badgeColor = getBadgeColor(status)

  const nextStyle = is_next ? 'opacity-70 border-dashed' : ''
  // cascade-v2 (B2): dezente Kennzeichnung automatisch ergänzter Lehrer —
  // gestrichelter Rand zusätzlich zur Statusfarbe (Farbtabelle bleibt einzige Quelle).
  const autoMatchedStyle = auto_matched && !is_next ? 'border-dashed' : ''
  // Subtile Pulsierung nur für den aktuell kontaktierten (live) Lehrer
  // (prionew-v2: auch beim ausstehenden Recontact — Handlungsbedarf signalisieren)
  const pulseStyle = (status === 'contacted' || status === 'recontacted') && !is_next ? 'animate-pulse' : ''

  // „X/Y"-Hinweis für Teilannahme (waiting)
  const coveredCount = coverageInfo?.coveredLessons?.length ?? 0
  const totalCount = coveredCount + (coverageInfo?.missingLessons?.length ?? 0)
  const waitingLabel = status === 'waiting' && totalCount > 0 ? `${coveredCount}/${totalCount}` : null

  const namePrefix = teacher_name ? `${teacher_name} — ` : ''
  const baseTitle = is_next
    ? `${namePrefix}Nächster: ${getBadgeLabel(status)}`
    : auto_matched
      ? `${namePrefix}Automatisch ergänzt: ${getBadgeLabel(status)}`
      : `${namePrefix}Priorität ${priorityRank}: ${getBadgeLabel(status)}`

  if (coverageInfo) {
    const allLessons = sortLessonsByDateTime([
      ...coverageInfo.coveredLessons.map(l => ({ ...l, covered: true })),
      ...coverageInfo.missingLessons.map(l => ({ ...l, covered: false })),
    ])
    const showDate = new Set(allLessons.map(l => l.date).filter(Boolean)).size > 1

    return (
      <div className="relative group shrink-0">
        <div className="relative">
          <div
            className={`w-10 h-10 rounded-full border-2 flex items-center justify-center text-xs font-semibold cursor-help ${badgeColor} ${nextStyle} ${autoMatchedStyle} ${pulseStyle}`}
            title={baseTitle}
          >
            <span>{teacherCode}</span>
            {waitingLabel && (
              <span className="ml-0.5 text-[9px] font-bold align-super">{waitingLabel}</span>
            )}
          </div>
          {!!coverageInfo?.affinityBonus && coverageInfo.affinityBonus > 0 && (
            <span
              className="absolute -top-1 -right-1 bg-amber-400 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center"
              title={`Affinität: +${coverageInfo.affinityBonus} Punkte`}
            >
              ★
            </span>
          )}
        </div>

        {allLessons.length > 0 && (
          <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-white dark:bg-gray-800 p-2 rounded-lg shadow-2xl z-[9999] border border-gray-200 dark:border-gray-700 whitespace-nowrap">
            <table className="text-xs border-collapse">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-600">
                  {showDate && (
                    <th className="px-2 py-1 text-left font-medium text-gray-500 dark:text-gray-400">Datum</th>
                  )}
                  <th className="px-2 py-1 text-left font-medium text-gray-500 dark:text-gray-400">Zeit</th>
                  <th className="px-2 py-1 text-left font-medium text-gray-500 dark:text-gray-400">Fach</th>
                  <th className="px-2 py-1 text-left font-medium text-gray-500 dark:text-gray-400">Klasse</th>
                </tr>
              </thead>
              <tbody>
                {allLessons.map((lesson, idx) => {
                  const status = resolveLessonStatus(lesson)
                  const style = getLessonStatusStyle(status)
                  return (
                    <tr key={lesson.id || idx} className={style.row}>
                      {showDate && (
                        <td className={`px-2 py-0.5 whitespace-nowrap ${style.text}`}>
                          {lesson.date
                            ? new Date(lesson.date).toLocaleDateString('de-DE', {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'numeric',
                              })
                            : '—'}
                        </td>
                      )}
                      <td className={`px-2 py-0.5 whitespace-nowrap ${style.text}`}>{lesson.time_slot_name || '—'}</td>
                      <td className={`px-2 py-0.5 whitespace-nowrap ${style.text}`}>{lesson.subject || '—'}</td>
                      <td className={`px-2 py-0.5 whitespace-nowrap ${style.text}`}>{lesson.klass || '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className={`w-10 h-10 rounded-full border-2 flex items-center justify-center text-xs font-semibold ${badgeColor} ${nextStyle} ${autoMatchedStyle} ${pulseStyle}`}
      title={baseTitle}
    >
      <span>{teacherCode}</span>
      {waitingLabel && (
        <span className="ml-0.5 text-[9px] font-bold align-super">{waitingLabel}</span>
      )}
    </div>
  )
}
